import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Layout, Space, Dropdown, Result } from 'antd';
import { CloseOutlined, EditFilled, PlusOutlined, SaveOutlined, FileTextOutlined, BarChartOutlined, EyeOutlined, SettingOutlined } from '@ant-design/icons';
import { Responsive } from 'react-grid-layout';
import Page from 'components/Page';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from 'hooks/apollo';
import GlobalFilter from 'components/GlobalFilter/GlobalFilter';
import { DEFAULT_FILTER } from 'components/GlobalFilter/FilterForm';
import { DashboardContentType } from 'shared/prisma/types';
import useGlobalContext from 'hooks/useGlobalContext';
import useCustomer from 'hooks/useCustomer';
import useDataSource from 'hooks/useDataSource';
import { useKeyPress, useSetState, useSize } from 'ahooks';
import { EDIT_CUSTOMER_DASHBOARD, EDIT_DASHBOARD, EDIT_REPORT } from 'shared/constants/permissions';
import useUser from 'hooks/useUser';
import { pick } from 'lodash';
import Loading from 'components/Loading';
import useCheckPermission from 'hooks/useCheckPermission';
import DashboardContent from './DashboardContent';
import EditDashboardContent from './EditDashboardContent';
import AddToDashboardDrawer from './AddToDashboardDrawer';
import EditDashboardDrawer from './EditDashboardDrawer';
import { getParentDashboardOptions } from './utils';
import { getDashboardCustomer, getDashboardWebsites } from './dashboardScope';

import './dashboard.less';

const filterFragment = gql`
  fragment Filter on GlobalFilter {
    id
    createdAt      
    title
    websiteBaseUrls
    allEnabled
    dateRangeConfig {
      ...DateRangeConfig
    }
    segments(orderBy: sortIndex_ASC) {
      id
      title
      enabled
      sortIndex
      query
      color
      dateRangeConfig {
        ...DateRangeConfig
      }
    }
    dataSources {
      id
    }
  }
  fragment DateRangeConfig on FilterDateRange {
    id
    period
    periodType
    lastNumberInput
    comparePeriod
    compareToPrevious
    compareToPreviousValue
    specificDates
    compareSpecificDates
  }
`;

const contentFragment = gql`
  fragment Content on DashboardContent {
    id
    title
    richText
    type
    layout
    filter {
      ...Filter
    }
    resultsConfig {
      id
      metrics
      dimensions
      filter
      settings
    }
  }
  ${filterFragment}
`;

const customerFragment = gql`
  fragment DashboardCustomer on Customer {
    id
    dataSources {
      id
      website {
        id
        url
        language
        languageName
        additionalLanguages {
          id
          url
          language
          languageName
        }
        additionalUrls
      }
    }
  }
`;

const dashboardFragments = gql`
  fragment Dashboard on Dashboard {
    id
    title
    parent
    settings
    page {
      id
      customer {
        ...DashboardCustomer
      }
      user { id }
      dashboards {
        id
        title
        parent
      }
    }  
    report {
      id
      customer {
        ...DashboardCustomer
      }
    }    
    filter {
      ...Filter
    }
    contents {
      ...Content        
    }
  }
  ${contentFragment}
  ${customerFragment}
`;

const DASHBOARD_QUERY = gql`
  query Dashboard($id: ID!) {
    dashboard(id: $id) {
      ...Dashboard
    }    
    config { report { baseUrl } }
  }
  ${dashboardFragments}
`;

const GLOBAL_FILTERS_QUERY = gql`
  query GlobalFilters($customerId: ID!) {   
    globalFilters(customerId: $customerId) {
      ...Filter
    }
  }
  ${filterFragment}
`;

const UPDATE_DASHBOARD_MUTATION = gql`
   mutation UpdateDashboard($id: ID!, $input: DashboardInput!) {
     updateDashboard(id: $id, input: $input) {
       ...Dashboard
     }
   }
   ${dashboardFragments}
 `;

const CREATE_DASHBOARD_CONTENT_MUTATION = gql`
   mutation CreateDashboardContent($dashboardId: ID!, $input: DashboardContentInput!) {
     createDashboardContent(dashboardId: $dashboardId, input: $input) {
       id
     }
   }
 `;

const UPDATE_DASHBOARD_CONTENT_MUTATION = gql`
   mutation UpdateDashboardContent($id: ID!, $input: DashboardContentInput!) {
     updateDashboardContent(id: $id, input: $input) {
       ...Content
     }
   }
   ${contentFragment}
 `;

const DELETE_DASHBOARD_MUTATION = gql`
   mutation DeleteDashboard($id: ID!) {
     deleteDashboard(id: $id) {
       id
     }
   }
 `;

const DELETE_DASHBOARD_CONTENT_MUTATION = gql`
   mutation DeleteDashboardContent($id: ID!) {
     deleteDashboardContent(id: $id) {
       id
     }
   }
 `;

const GridLayout = Responsive;

const rowHeight = 20;

const getCreateContentInput = (content) => {
  const input = {
    title: content.title,
    richText: content.richText,
    type: content.type,
    layout: {
      x: 0,
      y: 0,
      w: 12,
      h: 10,
      ...(content.layout || {}),
    },
  };

  if (content.type === DashboardContentType.Result) {
    input.resultsConfig = content.resultsConfig ? pick(content.resultsConfig, ['metrics', 'dimensions', 'filter', 'settings']) : {
      metrics: [{ metric: 'visits' }],
      settings: {
        rowLimit: 100,
        pageLimit: 10,
        type: 'table',
      },
    };
    input.filter = content.filter ? {
      allEnabled: content.filter.allEnabled,
      dateRangeConfig: content.filter.dateRangeConfig,
      segments: content.filter.segments.map(s => ({
        title: s.title,
        enabled: s.enabled,
        sortIndex: s.sortIndex,
        query: s.query,
        color: s.color,
        dateRangeConfig: s.dateRangeConfig,
      })),
      dataSourceIds: content.filter.dataSources?.map(ds => ds.id),
    } : undefined;
  }

  return input;
};

const breakpoints = {
  lg: 0,
};

const cols = {
  lg: 12,
};

const getContentLayout = (layout, contentId) => layout.lg?.find(l => l.i === contentId);
const getGridClassName = (isLayoutReady) => (isLayoutReady ? 'dashboard__grid dashboard__grid--ready' : 'dashboard__grid');

const getDashboardInput = (content, layout, editedContent) => content.map(c => {
  const contentLayout = getContentLayout(layout, c.id);

  return {
    ...editedContent[c.id],
    id: c.id,
    layout: contentLayout,
  };
});

const customerExtraFields = `
  dataSources {
    id
    website {
      id
      url
      language
      languageName
      additionalLanguages {
        id
        url
        language
        languageName
      }
      additionalUrls
    }
  }
`;

const Dashboard = ({ dashboardId, dashboardTitle, onChange, onDelete, canEditParent, breadcrumb, isReadOnly = false, header, footer, dashboardFilter, viewDate }) => {
  const { user, isSuperUser, hasPermission, hasCustomerPermission } = useUser();
  const { customerId: sessionCustomerId, customer: sessionCustomer, loading: customerLoading } = useCustomer({ extraFields: customerExtraFields, skip: isReadOnly });
  const { dataSourceId } = useDataSource();
  const checkPermission = useCheckPermission();
  const [updateDashboard, { loading: updating }] = useMutation(UPDATE_DASHBOARD_MUTATION);
  const [deleteDashboard] = useMutation(DELETE_DASHBOARD_MUTATION);
  const [createDashboardContent, { loading: creating }] = useMutation(CREATE_DASHBOARD_CONTENT_MUTATION);
  const [updateDashboardContent] = useMutation(UPDATE_DASHBOARD_CONTENT_MUTATION);
  const [deleteDashboardContent, { loading: removing }] = useMutation(DELETE_DASHBOARD_CONTENT_MUTATION);
  const { message } = useGlobalContext();
  const [layout, setLayout] = useSetState({});
  const [editDashboard, setEditDashboard] = useState(false);
  const [editSettings, setEditSettings] = useState(false);
  const [copyToDashboard, setCopyToDashboard] = useState(false);
  const [editedContent, setEditedContent] = useState({});
  const [globalFilterCollapsed, setGlobalFilterCollapsed] = useState(false);
  const gridContainerRef = useRef(null);
  const gridContainerSize = useSize(gridContainerRef);
  const [selectedGlobalFilterId, setSelectedGlobalFilterId] = useState(null);

  const { data: dashboardData, loading: dashboardLoading, refetch: dashboardRefetch } = useQuery(DASHBOARD_QUERY, {
    variables: { id: dashboardId },
  });

  const dashboard = dashboardData?.dashboard;

  const dashboardCustomer = getDashboardCustomer(dashboard);
  const customer = dashboardCustomer || sessionCustomer;
  const customerId = customer?.id || sessionCustomerId;

  const { data: globalFiltersData, loading: globalFiltersLoading, refetch: globalFiltersRefetch } = useQuery(GLOBAL_FILTERS_QUERY, {
    variables: { customerId },
    skip: isReadOnly || !customerId || dashboardLoading || dashboard?.filter,
  });

  useEffect(() => {
    setSelectedGlobalFilterId(null);
  }, [dashboardId]);

  useEffect(() => {
    if (dashboard?.filter || globalFiltersLoading) {
      return;
    }

    const filters = globalFiltersData?.globalFilters;
    if (!filters?.length) {
      return;
    }

    setSelectedGlobalFilterId((prev) => {
      if (prev && filters.some(f => f.id === prev)) {
        return prev;
      }
      return filters[0].id;
    });
  }, [dashboard?.filter, globalFiltersLoading, globalFiltersData?.globalFilters]);

  const globalFilter = dashboard?.filter
    || globalFiltersData?.globalFilters?.find(f => f.id === selectedGlobalFilterId)
    || DEFAULT_FILTER;

  const dataSourceIds = useMemo(() => (dataSourceId ? [dataSourceId] : globalFilter?.dataSources?.map(ds => ds.id) || []), [dataSourceId, globalFilter]);
  const websites = useMemo(() => getDashboardWebsites(customer), [customer]);

  const loading = dashboardLoading || globalFiltersLoading || (!dashboardCustomer && customerLoading);
  const content = dashboard?.contents || [];
  const gridWidth = gridContainerSize?.width || 0;
  const isLayoutReady = content.length === 0 || layout.lg?.length === content.length;

  const isReport = !!dashboard?.report;

  const canEdit = !isReadOnly && (dashboard?.page?.user?.id === user?.id
    || hasCustomerPermission(dashboard?.page?.customer?.id, EDIT_CUSTOMER_DASHBOARD)
    || hasCustomerPermission(dashboard?.report?.customer?.id, EDIT_REPORT)
    || hasPermission(EDIT_DASHBOARD)
    || dashboard?.report?.customer?.id === user?.customer?.id);

  useEffect(() => {
    setEditedContent(content.reduce((acc, c) => ({ ...acc, [c.id]: { title: c.title, richText: c.richText } }), {}));
  }, [dashboard]);

  useEffect(() => {
    if (content.length) {
      setLayout({
        lg: content.map((c) => {
          const contentLayout = getContentLayout(layout, c.id) || c.layout || { x: 0, y: 0, w: 12, h: 10 };

          return {
            i: c.id || c.i,
            ...contentLayout,
            h: editDashboard ? 5 : contentLayout.h,
            isDraggable: editDashboard,
            isResizable: editDashboard,
            minW: 3,
            maxW: 12,
            minH: 1,
          };
        }),
      });
    }
  }, [content, editDashboard]);

  const onAddContent = useCallback(async (contentData, targetDashboardId) => {
    const input = getCreateContentInput({
      ...contentData,
      title: contentData.title || (contentData.type === DashboardContentType.Result ? `Result ${content.length + 1}` : `Text ${content.length + 1}`),
      layout: {
        y: layout.lg?.length ? Math.max(...layout.lg.map(l => l.y)) + 8 : 0,
        ...(contentData.layout || {}),
      },
    });
    await createDashboardContent({ variables: { dashboardId: targetDashboardId || dashboardId, input } });
    dashboardRefetch();
  }, [dashboardId, content, layout]);

  useKeyPress('ctrl.v', async () => {
    if (!editDashboard && canEdit) {
      const text = await navigator.clipboard.readText();
      try {
        const clipboardData = JSON.parse(text);
        onAddContent(clipboardData);
      } catch (e) {
        message.error('Clipboard content could not be saved.');
        console.error(e);
      }
    }
  });

  const onSaveLayout = useCallback(async () => {
    await updateDashboard({
      variables: {
        id: dashboardId,
        input: {
          title: dashboard.title,
          parent: dashboard.parent,
          settings: dashboard.settings,
          contents: getDashboardInput(content, layout, editedContent),
        },
      },
    });
    setEditDashboard(false);
    onChange?.();
  }, [dashboardId, dashboard, content, layout, editedContent]);

  const onSettingsSubmit = useCallback(async (values) => {
    await updateDashboard({
      variables: {
        id: dashboardId,
        input: {
          title: values.title,
          parent: values.parent,
          settings: {
            ...dashboard.settings,
            dynamicContent: values.dynamicContent || null,
            sortIndex: values.sortIndex ?? null,
            ...(isSuperUser ? { viewPermission: values.viewPermission || null } : {}),
          },
          contents: content.map(c => ({
            id: c.id,
            title: c.title,
            richText: c.richText,
            layout: c.layout,
          })),
        },
      },
    });
    onChange?.();
  }, [dashboardId, content, dashboard?.settings, isSuperUser]);

  const onGlobalFilterChange = useCallback(async () => {
    await dashboardRefetch();
    await globalFiltersRefetch();
  }, [dashboardRefetch, globalFiltersRefetch]);

  const onRemoveGlobalFilter = useCallback(async () => {
    await globalFiltersRefetch();
  }, [globalFiltersRefetch]);

  const onRemoveDashboard = useCallback(async () => {
    await deleteDashboard({ variables: { id: dashboardId } });
    onDelete?.();
  }, [dashboardId]);

  const onLayoutChange = useCallback((newLayout) => {
    setLayout({ lg: newLayout });
  }, []);

  const onUpdateContent = useCallback(async (contentId, updates) => {
    await updateDashboardContent({
      variables: {
        id: contentId,
        input: updates,
      },
    });
  }, [updateDashboardContent]);

  const onCopyToDashboard = useCallback(async (contentData) => {
    setCopyToDashboard(contentData);
  }, []);

  const onCopyToDashboardSubmit = useCallback(async (targetDashboardId) => {
    await onAddContent(copyToDashboard, targetDashboardId);
    message.success('Content copied to dashboard');
    setCopyToDashboard(false);
  }, [copyToDashboard, onAddContent]);

  const removeContent = useCallback(async (id) => {
    if (id) {
      await deleteDashboardContent({ variables: { id } });
      dashboardRefetch();
    }
  }, []);

  const onContentSizeChange = useCallback((id, size) => {
    if (size?.height) {
      setLayout(prev => ({
        lg: prev.lg.map(l => (l.i === id ? {
          ...l,
          h: Math.ceil((size.height + 10) / (rowHeight + 10)),
        } : l)),
      }));
    }
  }, [rowHeight]);

  if (loading) {
    return <Loading />;
  }

  if (!dashboard) {
    return <Result status="info" title="Dashboard not found" />;
  }

  if (!checkPermission(dashboard.settings?.viewPermission)) {
    return <Result status="403" title="Access denied" subTitle="You do not have permission to view this dashboard." />;
  }

  const canOverrideFilter = canEdit && isReport;

  const parentOptions = getParentDashboardOptions(dashboardId, dashboard?.page?.dashboards);

  const contentTypeMenuItems = [
    {
      key: DashboardContentType.Result,
      label: 'Add Result',
      icon: <BarChartOutlined />,
      onClick: () => onAddContent({ type: DashboardContentType.Result }),
    },
    {
      key: DashboardContentType.Text,
      label: 'Add Text',
      icon: <FileTextOutlined />,
      onClick: () => onAddContent({ type: DashboardContentType.Text }),
    },
  ];

  const viewedTitle = dashboardTitle || dashboard?.title;

  return (
    <Layout style={isReadOnly ? {} : { marginRight: globalFilterCollapsed ? '42px' : '300px' }} className="dashboard">
      <Page>
        {header || (
          canEdit ? (
            <Page.Header
              breadcrumb={breadcrumb}
              title={viewedTitle}
              extra={editDashboard ? (
                <Space>
                  <Button
                    icon={<CloseOutlined />}
                    onClick={() => setEditDashboard(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={onSaveLayout}
                    loading={updating}
                  >
                    Save
                  </Button>
                </Space>
              ) : (
                <Space>
                  <Dropdown
                    menu={{
                      items: contentTypeMenuItems,
                    }}
                    trigger={['click']}
                  >
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      loading={creating}
                    >
                      Add content
                    </Button>
                  </Dropdown>
                  <Button
                    icon={<SettingOutlined />}
                    onClick={() => setEditSettings(true)}
                  >
                    Settings
                  </Button>
                  <Button
                    icon={<EditFilled />}
                    onClick={() => setEditDashboard(true)}
                  >
                    Edit layout
                  </Button>
                  {dashboard?.report && (
                    <a href={`${dashboardData?.config?.report?.baseUrl}view/${dashboard?.report?.id}`} target="_blank">
                      <Button icon={<EyeOutlined />}>Open sharable report</Button>
                    </a>
                  )}
                </Space>
              )}
            />
          ) : (
            <Page.Header
              title={viewedTitle}
            />
          ))}

        <div ref={gridContainerRef} className="dashboard__canvas">
          {content.length === 0 && (
            <Result status="info" title="Empty dashboard" style={{ maxWidth: '500px', margin: '0 auto' }}>
              <p>This dashboard is empty.</p>
              {canEdit && (
                <p>You can add content manually, or copy and paste content from other dashboards.</p>
              )}
            </Result>
          )}
          {gridWidth > 0 && (
            <GridLayout
              className={getGridClassName(isLayoutReady)}
              width={gridWidth}
              style={{
                '--grid-column-step': `${(gridWidth + 10) / cols.lg}px`,
                '--grid-column-width': `${(gridWidth - 10 * (cols.lg - 1)) / cols.lg}px`,
              }}
              layouts={layout}
              onLayoutChange={onLayoutChange}
              compactType="vertical"
              draggableHandle=".drag-handle"
              breakpoints={breakpoints}
              cols={cols}
              rowHeight={rowHeight}
              resizeHandles={['e']}
              useCSSTransforms
              containerPadding={[0, 0]}
            >
              {content.map((c) => (
                <div key={c.id || c.i}>
                  {editDashboard ? (
                    <EditDashboardContent
                      content={c}
                      removeContent={() => removeContent(c.id)}
                      removing={removing}
                      editedContent={editedContent[c.id]}
                      setEditedContent={ec => setEditedContent({ ...editedContent, [c.id]: { ...(editedContent[c.id] || {}), ...ec } })}
                      onSizeChange={onContentSizeChange}
                    />
                  ) : (
                    <DashboardContent
                      content={c}
                      dashboardFilter={dashboardFilter}
                      globalFilter={globalFilter}
                      onSizeChange={onContentSizeChange}
                      onUpdateContent={canEdit ? onUpdateContent : null}
                      canOverrideFilter={canOverrideFilter}
                      onUpdateFilter={dashboardRefetch}
                      onCopyToDashboard={isReadOnly ? null : onCopyToDashboard}
                      customerId={customerId}
                      dataSourceIds={dataSourceIds}
                      websites={websites}
                      viewDate={viewDate}
                      skipCurrencyQuery={isReadOnly}
                    />
                  )}
                </div>
              ))}
            </GridLayout>
          )}
        </div>

        {footer}
      </Page>
      {!isReadOnly && (
        <>
          <GlobalFilter
            onChange={onGlobalFilterChange}
            onSelectedFilterChange={setSelectedGlobalFilterId}
            onRemove={onRemoveGlobalFilter}
            filter={globalFilter}
            globalFilters={globalFiltersData?.globalFilters}
            websites={websites}
            collapsed={globalFilterCollapsed}
            onCollapse={setGlobalFilterCollapsed}
            customerId={customerId}
            title={dashboard?.report ? 'Report filter' : 'Filter'}
          />
          <AddToDashboardDrawer open={!!copyToDashboard} onSubmit={onCopyToDashboardSubmit} onCancel={() => setCopyToDashboard(false)} currentDashboardId={dashboardId} />
          <EditDashboardDrawer
            open={editSettings}
            dashboard={dashboard}
            canEditParent={canEditParent}
            parentOptions={parentOptions}
            isSuperUser={isSuperUser}
            onCancel={() => setEditSettings(false)}
            onSubmit={onSettingsSubmit}
            onRemove={onRemoveDashboard}
          />
        </>
      )}
    </Layout>
  );
};

export default Dashboard;
